import React, { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, FolderInput } from 'lucide-react';

import PageHeader from '../../../components/page/PageHeader';
import { useAuth } from '../../../context/AuthContext';
import { useWikiV2Client, wikiErrorStatus, wikiPageBody } from '../../../hooks/useWikiV2';
import WikiPageEditor from '../editor/WikiPageEditor';
import WikiEditorCheatSheet from '../editor/WikiEditorCheatSheet';
import WikiMarkdownView, { useWikiHeadings } from '../markdown/WikiMarkdownView';
import WikiCrumbs from '../category/WikiCrumbs';
import useWikiPlanAllows, { WIKI_PLAN_FEATURES, categoryAssistantOn } from '../category/wikiPlan';
import WikiFrame from '../shared/WikiFrame';
import WikiIcon from '../shared/WikiIcon';
import { WikiEmpty, WikiError, WikiLoading } from '../shared/WikiStates';
import useOpenWikiAsk from '../shared/useOpenWikiAsk';
import useWikiRole from '../shared/useWikiRole';
import WikiActionsMenu from '../shared/WikiActionsMenu';
import { archivePageConfirm, refreshAfterArchive } from '../shared/wikiManageActions';
import { WIKI_MANAGE_COPY, WIKI_MODAL_TOASTS } from '../modals/wikiModalCopy';
import { invalidateWiki, useWikiCategories, useWikiPage, wikiKeys } from '../shared/useWikiQueries';
import { dateLabel, versionLabel } from '../shared/wikiFormat';
import { wikiPaths } from '../shared/wikiRoutes';
import { wikiUi } from '../shared/wikiUi';
import { AckBanner, CompareCard, DraftBanner, PendingDraftBanner, SuggestedEdits } from './WikiPageNotices';
import { PageAccessCard, PageAcksCard, PageTocCard, PageVersionsCard } from './WikiPageRail';
import { PAGE_COPY, compareToast, isAckable, pageVersion } from './wikiPageCopy';
import './wikiPage.css';

const enc = encodeURIComponent;
/** Presigned asset URLs live 5 minutes (BE-core §4); refresh the map before that. */
const ASSET_MAP_STALE_MS = 4 * 60 * 1000;

/**
 * Page view — `/wiki/p/:id` (read) and `/wiki/p/:id/edit` (edit), spec B
 * §1.3 / §1.4 and C1 §1.2. Frame + type = the CRM Dashboard (REBUILD-PLAN §1);
 * structure, copy, colours and interactions = the prototype (`wkIsPage`).
 *
 * Main column (read): crumbs · PageHeader (title, owner · v · Updated
 *   [· Imported from], Required reading / Draft pills, Edit) · draft banner ·
 *   unpublished-changes banner (writers) · needs-ack / acknowledged banner ·
 *   suggested edit (owner + admins) · compare · the document card
 *   (WikiMarkdownView) · "Ask about this page".
 * Main column (edit): crumbs · FE-editor's `<WikiPageEditor page onExit/>`.
 * Rail (both): [edit: `<WikiEditorCheatSheet/>`] · ON THIS PAGE ·
 *   ACKNOWLEDGEMENTS (required pages) · VERSIONS.
 *
 * The page is read with `useWikiPage(id)` (key ['wiki','page',id]) so the
 * TopBar crumb and New-page location read the same cache entry.
 *
 * Plan (PLAN-CP): "Ask about this page" needs `wiki_category_assistants`
 * (its Ask is category-scoped), "Remind the rest" needs `wiki_acks`; both
 * show while the plan is unknown and the server decides. "Ask about this
 * page" also needs the page's category + subcategory `assistant_enabled`
 * (resolved from the shared categories cache; shown until it has loaded).
 */
export default function WikiPage({ pageId, mode = 'read' }) {
    const navigate = useNavigate();
    const role = useWikiRole();
    const { user } = useAuth();
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const openAsk = useOpenWikiAsk();
    const planAllows = useWikiPlanAllows();
    const categories = useWikiCategories();
    const pageQuery = useWikiPage(pageId);
    const page = pageQuery.data || null;
    const body = page ? wikiPageBody(page) : '';
    const headings = useWikiHeadings(body);
    const [compare, setCompare] = useState(null);

    // Images stored as `/api/wiki/assets/<id>` need the bearer token, so the
    // view swaps them for presigned URLs: one `GET /pages/{id}/assets` map per
    // page (cached; concurrent images share the request), falling back to the
    // per-asset JSON endpoint for an id the map doesn't have.
    const resolveAsset = useCallback(async (assetId) => {
        const map = await queryClient.fetchQuery({
            queryKey: [...wikiKeys.page(pageId), 'assets'],
            queryFn: () => client.get(`/wiki/pages/${enc(pageId)}/assets`),
            staleTime: ASSET_MAP_STALE_MS,
        }).catch(() => null);
        const hit = map && map.assets && map.assets[assetId];
        if (hit && hit.url) return hit.url;
        const one = await client.get(`/wiki/assets/${enc(assetId)}?format=json`);
        return (one && one.url) || null;
    }, [client, queryClient, pageId]);

    const onNavigate = useCallback((href) => navigate(href), [navigate]);
    const startEdit = useCallback(() => navigate(wikiPaths.pageEdit(pageId)), [navigate, pageId]);
    const exitEdit = useCallback(() => {
        invalidateWiki(queryClient, ['page', pageId], 'pages', 'home', 'ack', 'counts');
        navigate(wikiPaths.page(pageId));
    }, [queryClient, navigate, pageId]);
    const onCompare = useCallback((from) => {
        const to = Number(queryClient.getQueryData(wikiKeys.page(pageId))?.version) || 0;
        wikiUi.toast(compareToast(from, to));
        setCompare({ from, to });
    }, [queryClient, pageId]);

    if (pageQuery.isLoading) {
        return (
            <WikiFrame testId="wiki-page">
                <div className="wk-card"><WikiLoading label="Loading page…" /></div>
            </WikiFrame>
        );
    }
    if (!page) {
        // 404 and 403 answer the same "not available" state (no existence
        // leak); anything else is a real error with a retry.
        const status = wikiErrorStatus(pageQuery.error);
        if (pageQuery.isError && status !== 404 && status !== 403) {
            return (
                <WikiFrame testId="wiki-page">
                    <div className="wk-card"><WikiError error={pageQuery.error} onRetry={() => pageQuery.refetch()} /></div>
                </WikiFrame>
            );
        }
        return (
            <WikiFrame testId="wiki-page">
                <div className="wk-card" data-testid="wiki-page-not-found">
                    <WikiEmpty
                        icon="file-text"
                        title={PAGE_COPY.notFoundTitle}
                        detail={PAGE_COPY.notFoundBody}
                        action={(
                            <button type="button" className="wk-btn wk-btn--secondary wk-btn--sm" onClick={() => navigate(wikiPaths.home())}>
                                <WikiIcon name="house" size={14} /> Wiki home
                            </button>
                        )}
                    />
                </div>
            </WikiFrame>
        );
    }

    // The API is authoritative per page (`can_edit`); the role gate keeps the
    // UI honest for callers without wiki:write.
    const canEdit = role.canEdit && page.can_edit !== false;
    const editing = mode === 'edit';
    if (editing && !canEdit) return <Navigate to={wikiPaths.page(pageId)} replace />;

    const isOwner = Boolean(user && user.user_id && page.owner_user_id === user.user_id);
    const isReviewer = isOwner || role.canAdmin || page.access === 'admin';
    const published = page.status === 'published';
    const ackable = isAckable(page);
    const canAsk = planAllows(WIKI_PLAN_FEATURES.categoryAssistants) && categoryAssistantOn(
        categories.byId.get(page.category_id),
        page.subcategory_id ? categories.byId.get(page.subcategory_id) : null,
    );
    const title = page.title || 'Untitled page';

    // #872: Move and Archive for whoever can edit the page (read mode only).
    const openMove = () => wikiUi.openModal('movePage', { page });
    const openArchive = () => wikiUi.openModal('confirm', archivePageConfirm({
        page,
        onArchived: async () => {
            const here = page.subcategory_id || page.category_id;
            navigate(here ? wikiPaths.category(here) : wikiPaths.home());
            wikiUi.toast(WIKI_MODAL_TOASTS.pageArchived());
            await refreshAfterArchive(queryClient, wikiKeys.page(pageId));
        },
    }));

    const crumbs = [
        { label: 'Wiki', to: wikiPaths.home() },
        page.category_id && page.category_name ? { label: page.category_name, to: wikiPaths.category(page.category_id) } : null,
        page.subcategory_id && page.subcategory_name ? { label: page.subcategory_name, to: wikiPaths.category(page.subcategory_id) } : null,
        { label: title },
    ];

    const rail = (
        <aside className="wk-pg-rail" aria-label="About this page" data-testid="wiki-page-rail">
            {editing && <WikiEditorCheatSheet />}
            <PageTocCard headings={headings} interactive={!editing} />
            {ackable && <PageAcksCard page={page} canRemind={isReviewer && planAllows(WIKI_PLAN_FEATURES.acks)} />}
            <PageVersionsCard page={page} canEdit={canEdit} editing={editing} onCompare={onCompare} />
            {role.canAdmin && !editing && <PageAccessCard page={page} />}
        </aside>
    );

    if (editing) {
        return (
            <WikiFrame testId="wiki-page" data-mode="edit">
                <div className="wk-pg">
                    <div className="wk-pg-main" data-testid="wiki-page-main">
                        <WikiCrumbs items={crumbs} />
                        <WikiPageEditor page={page} onExit={exitEdit} />
                    </div>
                    {rail}
                </div>
            </WikiFrame>
        );
    }

    return (
        <WikiFrame testId="wiki-page" data-mode="read">
            <div className="wk-pg">
                <article className="wk-pg-main" data-testid="wiki-page-main">
                    <div className="wk-pg-top">
                        <WikiCrumbs items={crumbs} />
                        <div className="wk-pg-head">
                            <PageHeader
                                title={title}
                                subtitle={<PageMeta page={page} />}
                                right={canEdit ? (
                                    <>
                                        <button type="button" className="wk-btn wk-btn--secondary wk-btn--sm" data-testid="wiki-page-edit" onClick={startEdit}>
                                            <WikiIcon name="pencil" size={13} />
                                            {PAGE_COPY.edit}
                                        </button>
                                        <WikiActionsMenu
                                            size="sm"
                                            testId="wiki-page-actions"
                                            label={WIKI_MANAGE_COPY.menuLabel}
                                            items={[
                                                {
                                                    id: 'move', label: WIKI_MANAGE_COPY.move, icon: FolderInput,
                                                    onSelect: openMove, testId: 'wiki-page-move', action: 'wiki.move_page',
                                                },
                                                {
                                                    id: 'archive', label: WIKI_MANAGE_COPY.archive, icon: Archive, danger: true,
                                                    onSelect: openArchive, testId: 'wiki-page-archive', action: 'wiki.archive_page',
                                                },
                                            ]}
                                        />
                                    </>
                                ) : null}
                                testId="wiki-page-header"
                            />
                        </div>
                    </div>

                    {page.status === 'draft' && <DraftBanner canEdit={canEdit} onEdit={startEdit} />}
                    {published && page.has_draft && canEdit && <PendingDraftBanner onEdit={startEdit} />}
                    {ackable && <AckBanner page={page} />}
                    {published && isReviewer && <SuggestedEdits page={page} canEdit={canEdit} />}
                    {compare && (
                        <CompareCard pageId={pageId} from={compare.from} to={compare.to} onClose={() => setCompare(null)} />
                    )}

                    <div className="wk-card wk-card--roomy wk-pg-doc" data-testid="wiki-page-body">
                        {body.trim()
                            ? <WikiMarkdownView markdown={body} resolveAsset={resolveAsset} onNavigate={onNavigate} />
                            : <p className="wk-t-meta wk-pg-doc-empty">{PAGE_COPY.emptyBody}</p>}
                    </div>

                    {canAsk && (
                        <button
                            type="button"
                            className="wk-pg-ask"
                            data-testid="wiki-page-ask"
                            onClick={() => openAsk({ categoryId: page.category_id || null })}
                        >
                            <span className="wk-pg-ask-tile" aria-hidden="true"><WikiIcon name="sparkles" size={16} /></span>
                            <span className="wk-pg-ask-text">
                                <span className="wk-t-name">{PAGE_COPY.askTitle}</span>
                                <span className="wk-t-meta wk-pg-ask-sub">{PAGE_COPY.askBody}</span>
                            </span>
                            <WikiIcon name="chevron-right" size={16} />
                        </button>
                    )}
                </article>
                {rail}
            </div>
        </WikiFrame>
    );
}

/** Meta line: owner · v4 · Updated Oct 2 [· Imported from file.md] + pills (§1.3 b). */
function PageMeta({ page }) {
    const parts = [];
    if (page.owner_name) parts.push(page.owner_name);
    parts.push(versionLabel(pageVersion(page)));
    const updated = dateLabel(page.updated_at);
    if (updated) parts.push(`Updated ${updated}`);
    const imported = page.imported_from && page.imported_from.filename;
    if (imported) parts.push(`Imported from ${imported}`);
    return (
        <span className="wk-pg-meta" data-testid="wiki-page-meta">
            {parts.map((part, index) => (
                <React.Fragment key={`${index}-${part}`}>
                    {index > 0 && <span className="wk-pg-meta-sep" aria-hidden="true">·</span>}
                    <span>{part}</span>
                </React.Fragment>
            ))}
            {page.required_ack && <span className="wk-pill is-outline">{PAGE_COPY.requiredPill}</span>}
            {page.status === 'draft' && <span className="wk-pill">{PAGE_COPY.draftPill}</span>}
        </span>
    );
}
